import { HttpErrorResponse } from "@angular/common/http";
import { Injector } from "@angular/core";
import { MatTableDataSource } from "@angular/material";
import { BehaviorSubject, combineLatest, Observable, of } from "rxjs";
import { filter, map, tap } from "rxjs/operators";
import { ObservableSpreading } from "../../interface/type/observable-spreading.type";
import { CampaignProduct } from "../../persistence/dto/campaign-product.model";
import { Campaign } from "../../persistence/dto/campaign.model";
import { Discount } from "../../persistence/dto/discount.model";
import { OrderEventOriginType } from "../../persistence/dto/order-event-origin-type.enum";
import { OrderEventType } from "../../persistence/dto/order-event-type.model";
import { OrderEvent } from "../../persistence/dto/order-event.model";
import { OrderItemStatus } from "../../persistence/dto/order-item-status.enum";
import { OrderEventForCreation } from "../../persistence/view-dto/order-event-for-creation.model";
import { OrderEventForGet } from "../../persistence/view-dto/order-event-for-get.model";
import { OrderEventItemForCreation } from "../../persistence/view-dto/order-event-item-for-creation.model";
import { OrderEventItemForGet } from "../../persistence/view-dto/order-event-item-for-get.model";
import { OrderEventProductsForCreation } from "../../persistence/view-dto/order-event-products-for-creation.model";
import { OrderEventSearch } from "../../persistence/view-dto/order-event-search.model";
import { OrderEventTrackingForGet } from "../../persistence/view-dto/order-event-tracking-for-get.model";
import { OrderItem } from "../../persistence/view-dto/order-item.model";
import { Order } from "../../persistence/view-dto/order.model";
import { ReplacementItemForCreation } from "../../persistence/view-dto/replacement-item-for-creation.model";
import { CampaignProductService } from "../../service/campaign-product.service";
import { CampaignService } from "../../service/campaign.service";
import { DiscountService } from "../../service/discount.service";
import { OrderEventTypeService } from "../../service/order-event-type.service";
import { OrderEventService } from "../../service/order-event.service";
import { OrderService } from "../../service/order.service";
import { Optional } from "../../support/optional.model";
import { TypeSystem } from "../../support/type-system.support";
import { Unsubscriber } from "../../support/unsubscriber.support";
import { AdvancedForm } from "../advanced-form.model";
import { Logger } from "../logger.model";
import { andSpread } from "../operator/and-spread.operator";
import { OrderEventTypeId } from "./order-event-type-id.enum";


	/**
	 * @description
	 *	Lógica completa de notas de crédito (NC), y de cambios y reclamos
	 *	(CyR). Este motor se encarga de gestionar la creación de las mismas y
	 *	de proveer una interfaz simplificada para los componentes de la UI.
	 */

export class OrderEventEngine {

	// Estado inmutable:
	private _advancedForm: AdvancedForm;
	private _injector: Injector;
	private _logger: Logger;

	// Behaviors (nótese que todo lo que no tiene valor inicial, queda dentro de una instancia de Optional):
	private _amount = new BehaviorSubject<number>(0);
	private _availableOrderEventTypes = new BehaviorSubject<OrderEventType[]>([]);
	private _campaign = new BehaviorSubject<Optional<Campaign>>(Optional.empty());
	private _campaignProducts = new BehaviorSubject<CampaignProduct[]>([]);
	private _controlStatus = new BehaviorSubject<boolean>(false);
	private _discount = new BehaviorSubject<Optional<Discount>>(Optional.empty());
	private _discountDoesNotChange = new BehaviorSubject<boolean>(true);
	private _existingOrderEvents = new BehaviorSubject<OrderEvent[]>([]);
	private _hasMinimumOrderAmount = new BehaviorSubject<boolean>(false);
	private _hasShippingAmountCovered = new BehaviorSubject<boolean>(true);
	private _isOverOriginalAmount = new BehaviorSubject<boolean>(false);
	private _isReadyForCreation = new BehaviorSubject<boolean>(false);
	private _minimumOrderAmount = new BehaviorSubject<number>(0);
	private _needProductReplacement = new BehaviorSubject<boolean>(false);
	private _needProductReturn = new BehaviorSubject<boolean>(false);
	private _order = new BehaviorSubject<Optional<Order>>(Optional.empty());
	private _orderEventForGet = new BehaviorSubject<Optional<OrderEventForGet>>(Optional.empty());
	private _orderEventProducts =  new BehaviorSubject<MatTableDataSource<CampaignProduct>>(new MatTableDataSource([]));
	private _orderEventSearch = new BehaviorSubject<Optional<OrderEventSearch>>(Optional.empty());
	private _orderEventTrackings = new BehaviorSubject<MatTableDataSource<OrderEventTrackingForGet>>(new MatTableDataSource([]));
	private _orderItems = new BehaviorSubject<MatTableDataSource<OrderItem>>(new MatTableDataSource([]));
	private _remainingOrder = new BehaviorSubject<Optional<Order>>(Optional.empty());
	private _replacementItems = new BehaviorSubject<MatTableDataSource<CampaignProduct>>(new MatTableDataSource([]));
	private _totalAmountAtBrochurePrice = new BehaviorSubject<number>(0);
	private _totalAmountAtInvoicePrice = new BehaviorSubject<number>(0);
	private _totalOrderEventBrochurePrice = new BehaviorSubject<number>(0);
	private _totalOrderEventInvoicePrice = new BehaviorSubject<number>(0);
	private _totalOrderEventProductsBrochurePrice = new BehaviorSubject<number>(0);
	private _totalReplacementItemsBrochurePrice = new BehaviorSubject<number>(0);
	private _unsubscriber = new Unsubscriber();

	// Servicios:
	private _campaignProductService: CampaignProductService;
	private _campaignService: CampaignService;
	private _discountService: DiscountService;
	private _orderEventService: OrderEventService;
	private _orderEventTypeService: OrderEventTypeService;
	private _orderService: OrderService;

	// Estado adicional mutable:
	private orderEventProductQuantities = new Map<number, number>();
	private replacementItemQuantities = new Map<number, number>();

	// Estado adicional inmutable:
	private _causesWithAutoInsertion = new Set<OrderEventTypeId>();

	public constructor(builder: OrderEventEngineBuilder) {
		// Estado inmutable; no todo va a estar disponible:
		this._advancedForm = builder._advancedForm;
		this._injector = builder._injector;
		this._logger = builder._logger;

		// Servicios:
		this._campaignProductService = this._injector.get(CampaignProductService);
		this._campaignService = this._injector.get(CampaignService);
		this._discountService = this._injector.get(DiscountService);
		this._orderEventService = this._injector.get(OrderEventService);
		this._orderEventTypeService = this._injector.get(OrderEventTypeService);
		this._orderService = this._injector.get(OrderService);

		// Estado adicional mutable:
		this._orderEventTypeService.allTypes()
			.subscribe({
				next: orderEventTypes => {
					this._availableOrderEventTypes.next(orderEventTypes);
				},
				error: (error: Error) => this._logger.error(error)
			});

		// Estado adicional inmutable:
		this._causesWithAutoInsertion
			.add(OrderEventTypeId.BrokenCaseOrContainer)
			.add(OrderEventTypeId.DefectiveProduct)
			.add(OrderEventTypeId.ContentIncomplete)
			.add(OrderEventTypeId.ProductInvoicedDoesNotMatchProductShipped)
			.add(OrderEventTypeId.ProductInvoicedAndNotShipped);

		// Máquina de estados:
		this._unsubscriber
			.addSubscription(combineLatest([this.totalAmountAtInvoicePrice(), this.discount()])
				.subscribe({
					next: ([totalAmountAtInvoicePrice, discount]) => {
						this._logger.debug(
							"discountDoesNotChange? = fromAmount <= totalAmountAtInvoicePrice <= toAmount =",
							discount.fromAmount, totalAmountAtInvoicePrice, discount.toAmount
						);
						this._discountDoesNotChange.next(discount.fromAmount <= totalAmountAtInvoicePrice
							&& totalAmountAtInvoicePrice <= discount.toAmount);
					},
					error: (error: Error) => this._logger.error(error)
				}))
			.addSubscription(combineLatest([this.totalAmountAtBrochurePrice(), this.campaign()])
				.subscribe({
					next: ([totalAmountAtBrochurePrice, campaign]) => {
						this._order.value
							.ifPresent(order => {
								this._hasShippingAmountCovered.next(order.shippingAmount === 0
									&& campaign.minOrderShipping <= totalAmountAtBrochurePrice);
							})
							// Si no hay pedido, por defecto cubre los gastos, ya que no hay que enviar nada.
							.orElse(() => this._hasShippingAmountCovered.next(true));
					},
					error: (error: Error) => this._logger.error(error)
				}))
			.addSubscription(combineLatest([this.orderItems(), this.replacementItems()])
				.subscribe({
					next: ([orderItemsDataSource, replacementItemsDataSource]) => {
						const totalOrderItemsPrice = orderItemsDataSource.data
							.map(orderItem => orderItem.quantity * this.campaignPriceByProductCode(orderItem.productCode)
								.getOrElse(orderItem.price))
							.reduce((accumulator, price) => accumulator + price, 0);
						const totalReplacementItemsPrice = replacementItemsDataSource.data
							.map(campaignProduct => this.replacementItemQuantity(campaignProduct) * this.campaignPriceByProductCode(campaignProduct.product.code).getOrElse(0))
							.reduce((accumulator, price) => accumulator + price, 0);
						this._isOverOriginalAmount.next(1.1 * totalOrderItemsPrice < totalReplacementItemsPrice);
					},
					error: (error: Error) => this._logger.error(error)
				}))
			.addSubscription(this.totalAmountAtBrochurePrice()
				.subscribe({
					next: totalAmountAtBrochurePrice => {
						this._hasMinimumOrderAmount
							.next(this._minimumOrderAmount.value <= totalAmountAtBrochurePrice);
					},
					error: (error: Error) => this._logger.error(error)
				}))
			.addSubscription(this.isValid()
				.subscribe({
					next: isValid => this._isReadyForCreation.next(isValid),
					error: (error: Error) => this._logger.error(error)
				}))
			.addSubscription(this.netChangeOverApplicationUserId()
				.subscribe({
					next: _ => {
						this._logger.debug("applicationUserId:", _.applicationUserId);
						_.controlStatus.ifPresent(controlStatus => this._controlStatus.next(controlStatus));
						this._order.next(Optional.empty());
					},
					error: (error: Error) => this._logger.error(error)
				}))
			.addSubscription(this.netChangeIfPresentDescriptionEvent()
				.subscribe({
					next: (descriptionEvent: string) => {},
					error: (error: Error) => this._logger.error(error)
				}))
			.addSubscription(this._advancedForm.netChangeIfPresent("generateCreditNote")
				.subscribe({
					next: (generateCreditNote: boolean) => {},
					error: (error: Error) => this._logger.error(error)
				}))
			.addSubscription(this._advancedForm.netChangeIfPresent("orderEventOriginType")
				.subscribe({
					next: (orderEventOriginType: OrderEventOriginType) => {},
					error: (error: Error) => this._logger.error(error)
				}))
			.addSubscription(this._advancedForm.netChangeIfPresent("orderEventTypeId")
				.subscribe({
					next: (orderEventTypeId: number) => {
						this._logger.debug("orderEventTypeId", orderEventTypeId);
						if (orderEventTypeId === OrderEventTypeId.WaiverToProductsPurchasedWithPaymentCycles) {
							this._advancedForm.patch({ "generateCreditNote": true });
						}
						this._needProductReplacement.next(orderEventTypeId !== OrderEventTypeId.WaiverToProductsPurchasedWithPaymentCycles);
						this._needProductReturn.next(orderEventTypeId === OrderEventTypeId.ProductInvoicedDoesNotMatchProductShipped);
						OrderEventEngine.trigger(this._order);
					},
					error: (error: Error) => this._logger.error(error)
				}))
			.addSubscription(this.netChangeIfPresentOrderId()
				.subscribe({
					next: _ => {
						this._logger.debug("order", _.fromOrder.order);
						this._campaign.next(Optional.of(_.fromOrder.campaign));
						this._campaignProducts.next(_.fromOrder.campaignProducts);
						this._discount.next(Optional.of(_.discount));
						this._existingOrderEvents.next(_.fromOrder.existingOrderEvents);
						if (!Optional.of(this._orderEventForGet.value).isPresent()) {
							this.orderEventProductQuantities.clear();
							this.replacementItemQuantities.clear();
						}
						this._order.next(Optional.of(_.fromOrder.order));
					},
					error: (error: Error) => this._logger.error(error)
				}))
			.addSubscription(this.campaign()
				.subscribe({
					next: campaign => {
						this._minimumOrderAmount.next(campaign.minOrder);
					},
					error: (error: Error) => this._logger.error(error)
				}))
			.addSubscription(this.orderEventForGet()
				.subscribe({
					next: orderEventForGet => {
						// 'this._orderItems' se actualiza en 'this.order()'.
						const orderEventProducts = orderEventForGet.orderEventProducts
							.map(orderEventProduct => {
								this.orderEventProductQuantities.set(orderEventProduct.campaignProductReturnId, orderEventProduct.quantity);
								return orderEventProduct.asCampaignProduct();
							});
						const replacementItems = orderEventForGet.replacementItems
							.map(replacementItem => {
								this.replacementItemQuantities.set(replacementItem.campaignProductReplacementId, replacementItem.quantity);
								return replacementItem.asCampaignProduct();
							});
						this._orderEventProducts.next(new MatTableDataSource(orderEventProducts));
						this._orderEventTrackings.next(new MatTableDataSource(orderEventForGet.orderEventTrackings));
						this._replacementItems.next(new MatTableDataSource(replacementItems));
					},
					error: (error: Error) => this._logger.error(error)
				}))
			.addSubscription(this.orderEventSearch()
				.subscribe({
					next: orderEventSearch => this._amount.next(orderEventSearch.amount),
					error: (error: Error) => this._logger.error(error)
				}))
			.addSubscription(this.orderEventProducts()
				.pipe(map(dataSource => dataSource.data))
				.subscribe({
					next: orderEventProducts => {
						this._totalOrderEventProductsBrochurePrice.next(orderEventProducts
							.map(orderEventProduct => this.orderEventProductTotalBrochurePrice(orderEventProduct))
							.reduce((accumulator, totalBrochurePrice) => accumulator + totalBrochurePrice, 0));
					},
					error: (error: Error) => this._logger.error(error)
				}))
			.addSubscription(this.orderItems()
				.pipe(map(dataSource => dataSource.data))
				.subscribe({
					next: orderItems => {
						this._totalOrderEventBrochurePrice.next(orderItems
							.map(orderItem => orderItem.totalBrochurePrice())
							.reduce((accumulator, totalBrochurePrice) => accumulator + totalBrochurePrice, 0));
						this._totalOrderEventInvoicePrice.next(orderItems
							.map(orderItem => orderItem.totalInvoicePrice())
							.reduce((accumulator, totalInvoicePrice) => accumulator + totalInvoicePrice, 0));
					},
					error: (error: Error) => this._logger.error(error)
				}))
			.addSubscription(this.replacementItems()
				.pipe(map(dataSource => dataSource.data))
				.subscribe({
					next: replacementItems => {
						// Se computan al precio de los ítems del pedido, y el
						// resto, como vienen de la campaña.
						this._totalReplacementItemsBrochurePrice.next(replacementItems
							.map(replacementItem => this.replacementItemTotalBrochurePrice(replacementItem))
							.reduce((accumulator, totalBrochurePrice) => accumulator + totalBrochurePrice, 0));
					},
					error: (error: Error) => this._logger.error(error)
				}))
			.addSubscription(this.order()
				.subscribe({
					next: optionalOrder => {
						// Copia en profundidad:
						const remainingOrder = optionalOrder
							.map(order => Order.from(order))
							.map(order => {
								// Se debe garantizar que existe cada vez que se modifica 'order':
								this._existingOrderEvents.value
									.filter(orderEvent => orderEvent.active)
									.forEach(orderEvent => order.applyOrderEvent(orderEvent));
								return order;
							});
						this._remainingOrder.next(remainingOrder);
						/**
						 * Se supone que ya se cargó, porque orderId se
						 * extraería de este lugar. Esto hay que mejorarlo.
						 * Falla de otro modo porque si OrderEventForGet ya se
						 * emite antes de Order, entonces nunca se ve este
						 * evento.
						 */
						this._orderEventForGet.value
							.ifPresent(orderEventForGet => {
								optionalOrder.ifPresent(order => {
									const orderItems = order.orderItems
										.filter(orderItem => {
											let _orderEventItem: OrderEventItemForGet = undefined;
											const match = orderEventForGet.orderEventItems
												.some(orderEventItem => {
													_orderEventItem = orderEventItem;
													const match = orderEventItem.code === orderItem.productCode;
													return match;
												});
											if (match) {
												// Al hacer esto no se está cambiando el pedido original?
												orderItem.quantity = _orderEventItem.quantity;
											}
											return match;
										});
									this._orderItems.next(new MatTableDataSource(orderItems));
								});
							})
							.orElse(() => {
								this._orderEventProducts.next(new MatTableDataSource([]));
								this._orderItems.next(new MatTableDataSource([]));
								this._replacementItems.next(new MatTableDataSource([]));
							});
						// Esta nueva dependencia se debe a que ahora el precio
						// de campaña depende de los ítems en el pedido:
						OrderEventEngine.trigger(this._replacementItems);
					},
					error: (error: Error) => this._logger.error(error)
				}))
			.addSubscription(this._remainingOrder
				.subscribe({
					next: optionalRemainingOrder => {
						optionalRemainingOrder.ifPresent(remainingOrder => {
							this._totalAmountAtBrochurePrice.next(remainingOrder.totalAmountAtBrochurePrice());
							this._totalAmountAtInvoicePrice.next(remainingOrder.totalAmountAtInvoicePrice());
						});
					},
					error: (error: Error) => this._logger.error(error)
				}));
	}

	private campaignPriceByProductCode(productCode: string): Optional<number> {
		return Optional.of(this._campaignProducts.value
			.find(campaignProduct => campaignProduct.product.code === productCode))
			.map(campaignProduct => campaignProduct.price);
	}

	////////////////// PRIVATE OBSERVATIONS
	private discount(): Observable<Discount> {
		return this._discount.asObservable()
			.pipe(
				filter(optionalDiscount => optionalDiscount.isPresent()),
				map(optionalDiscount => optionalDiscount.value)
			);
	}

	private campaign(): Observable<Campaign> {
		return this._campaign.asObservable()
			.pipe(
				filter(optionalCampaign => optionalCampaign.isPresent()),
				map(optionalCampaign => optionalCampaign.value)
			);
	}

	private orderEventForGet(): Observable<OrderEventForGet> {
		return this._orderEventForGet.asObservable()
			.pipe(
				filter(optionalOrderEventForGet => optionalOrderEventForGet.isPresent()),
				map(optionalOrderEventForGet => optionalOrderEventForGet.value)
			);
	}

	private orderEventSearch(): Observable<OrderEventSearch> {
		return this._orderEventSearch.asObservable()
			.pipe(
				filter(optionalOrderEventSearch => optionalOrderEventSearch.isPresent()),
				map(optionalOrderEventSearch => optionalOrderEventSearch.value)
			);
	}

	private netChangeIfPresentOrderId(): Observable<ObservableSpreading<{
		discount: Observable<Discount>,
		fromOrder: Observable<ObservableSpreading<{
			order: Observable<Order>,
			campaign: Observable<Campaign>,
			campaignProducts: Observable<CampaignProduct[]>,
			existingOrderEvents: Observable<OrderEvent[]>
		}>>
	}>> {
		return this._advancedForm.netChangeIfPresent("orderId")
			.pipe(andSpread((orderId: number) => ({
				discount: this._discountService.discountByOrderID(orderId),
				fromOrder: this._orderService.orderByID(orderId)
					.pipe(andSpread(_ => ({
						order: of(_),
						campaign: this._campaignService.campaignByID(_.campaignId),
						campaignProducts: this._campaignProductService.allByCampaignId(_.campaignId),
						existingOrderEvents: this._orderEventService.orderEventsByOrderID(orderId)
					})))
				})
			));
	}

	private isValid(): Observable<boolean> {
		return combineLatest([
				this.orderItems(), this.replacementItems(),
				this.orderEventProducts(), this._advancedForm.isValid()])
			.pipe(
				map(([oi, ri, oep, isValid]) => isValid && 0 < oi.data.length));
	}

	private netChangeIfPresentDescriptionEvent(): Observable<any> {
		return this._advancedForm.netChangeIfPresent("descriptionEvent");
	}

	private netChangeOverApplicationUserId(): Observable<ObservableSpreading<{
		applicationUserId: Observable<string>,
		controlStatus: Observable<Optional<boolean>>
	}>> {
		return this._advancedForm.netChangeOver("applicationUserId")
			.pipe(
				map(netChange => netChange.delta["applicationUserId"]),
				andSpread((applicationUserId: string) => ({
					applicationUserId: of(applicationUserId),
					controlStatus: this._orderEventService.tryCheckControlStatusByUserID(applicationUserId)
				})
			))
	};
	////////////////// PRIVATE OBSERVATIONS

	/**
	 * @description
	 *	Devuelve el monto total de la nota.
	 *
	 * @returns
	 *	El monto total de la nota.
	 */
	public amount(): Observable<number> {
		return this._amount.asObservable();
	}

	public orderAmount(): Observable<number> {
		return this._order.asObservable()
			.pipe(
				map(optionalOrder => optionalOrder
					.map(order => order.totalAmount)
					.getOrElse(0))
			);
	}

	public availableCampaignProducts(): Observable<CampaignProduct[]> {
		return this._campaignProducts.asObservable()
			.pipe(
				map(campaignProducts => campaignProducts
					.sort((campaignProduct1, campaignProduct2) => +campaignProduct1.product.code - +campaignProduct2.product.code)));
	}

	public availableOrderItems(): Observable<OrderItem[]> {
		return this._remainingOrder.asObservable()
			.pipe(
				filter(remainingOrder => remainingOrder.isPresent()),
				map(remainingOrder => remainingOrder.value.orderItems
					.filter(orderItem => 0 < orderItem.quantity)
					.filter(orderItem => {
						const orderEventTypeId = this._advancedForm.valueOf("orderEventTypeId");
						switch (orderEventTypeId) {
							case OrderEventTypeId.WaiverToProductsPurchasedWithPaymentCycles: {
								return !orderItem.inLastPaymentCycle();
							}
							default: {
								return orderItem.inLastPaymentCycle();
							}
						}
					})
					.filter(orderItem => orderItem.orderItemStatus !== OrderItemStatus.NoStock)
					.filter(orderItem => !(orderItem.price == 0  && (orderItem.rewardId == null || orderItem.rewardId == 0)))
					.sort((orderItem1, orderItem2) => +orderItem1.productCode - +orderItem2.productCode))
		);
	}

	/**
	 * @description
	 *	Obtiene las causas de notas disponibles, descartando las ocasionadas
	 *	por desarmes.
	 *
	 * @returns
	 *	Las causas de nota disponibles. No es un observable ya que esta lista
	 *	es estática a lo largo de la creación o edición de una nota.
	 */
	public availableOrderEventTypes(): Observable<OrderEventType[]> {
		return this._availableOrderEventTypes.asObservable();
	}

	/**
	 * @description
	 *	El conjunto de causas que poseen auto-inserción del ítem de reemplazo,
	 *	para las cuales no es posible modificar la lista ReplacementItems del
	 *	modelo asociado.
	 */
	public causesWithAutoInsertion(): Set<OrderEventTypeId> {
		return new Set<OrderEventTypeId>(this._causesWithAutoInsertion);
	}

	/**
	 * @description
	 *	.
	 *
	 * @returns
	 *	.
	 */
	public controlStatus(): Observable<boolean> {
		return this._controlStatus.asObservable();
	}

	public discountDoesNotChange(): Observable<boolean> {
		return this._discountDoesNotChange.asObservable();
	}

	/**
	 * @description
	 *	Emite todos los CyR/NC asociados al pedido actual, se encuentren en
	 *	estado activo o no (borrado lógico).
	 *
	 * @returns
	 *	Un observable de la lista de CyR/NC.
	 */
	public existingOrderEvents(): Observable<OrderEvent[]> {
		return this._existingOrderEvents.asObservable();
	}

	/**
	 * @description
	 *	Descarga una nota existente y actualiza el motor para representar el
	 *	estado de la misma.
	 *
	 * @returns
	 *	Esta misma instancia.
	 */
	public forOrderEvent(orderEventId: number): OrderEventEngine {
		TypeSystem.async()
			.pipe(andSpread(_ => ({
				orderEventSearch: this._orderEventService.findOrderEventByID(orderEventId),
				orderEventForGet: this._orderEventService.orderEventByID(orderEventId)
			})))
			.subscribe({
				next: _ => {
					this._logger.debug("Loading existing orderEvent:", _);
					// TODO: 'descriptionEvent' no se encuentra en el modelo, pero sí se envía al crear la nota.
					// Lo ingresado al crear la nota, luego se visualiza en el detalle del OrderEventTracking (el campo es 'descriptionStatus').
					this._advancedForm.patchInOrder([
						{ "applicationUserId": _.orderEventForGet.applicationUserId },
						{ "descriptionEvent": "[No hay ninguna descripción asociada a esta nota.]" },
						{ "generateCreditNote": _.orderEventForGet.generateCreditNote },
						{ "orderEventOriginType": _.orderEventForGet.orderEventOriginType },
						{ "orderEventTypeId": _.orderEventForGet.orderEventTypeId },
						{ "orderId": _.orderEventForGet.orderId }
					]);
					this._orderEventForGet.next(Optional.of(_.orderEventForGet));
					this._orderEventSearch.next(Optional.of(_.orderEventSearch));
				},
				error: (error: HttpErrorResponse) => {
					this._logger.error(error);
				}
			});
		return this;
	}

	public hasMinimumOrderAmount(): Observable<boolean> {
		return this._hasMinimumOrderAmount.asObservable();
	}

	public hasShippingAmountCovered(): Observable<boolean> {
		return this._hasShippingAmountCovered.asObservable();
	}

	public isOverOriginalAmount(): Observable<boolean> {
		return this._isOverOriginalAmount.asObservable();
	}

	/**
	 * @description
	 *	Determina si el estado actual de la nota en composición es válido para
	 *	creación o no, como un observable.
	 *
	 * @returns
	 *	Verdadero si la siguiente ejecución del método
	 *	'loadOrderEventForCreation(.)' producirá un payload que permita crear
	 *	una nueva nota de CyR o NC según corresponda.
	 */
	public isReadyForCreation(): Observable<boolean> {
		return this._isReadyForCreation.asObservable();
	}

	/**
	 * @description
	 *	Construye un payload para crear la nota.
	 *
	 * @returns
	 *	El payload, listo para ser enviado al servidor.
	 */
	public loadOrderEventForCreation(): OrderEventForCreation {
		const orderEventForCreation = OrderEventForCreation.empty();
		this._advancedForm.load(orderEventForCreation);
		orderEventForCreation.orderEventItems = this._orderItems.value.data
			.map(orderItem => OrderEventItemForCreation.fromOrderItem(orderItem));
		orderEventForCreation.orderEventProducts = this._orderEventProducts.value.data
			.map(campaignProduct => OrderEventProductsForCreation
				.fromCampaignProduct(campaignProduct, this.orderEventProductQuantity(campaignProduct)));
		orderEventForCreation.replacementItems = this._replacementItems.value.data
			.map(campaignProduct => ReplacementItemForCreation
				.fromCampaignProduct(campaignProduct, this.replacementItemQuantity(campaignProduct)));
		return orderEventForCreation;
	}

	/**
	 * @description
	 *	Computa el monto mínimo que un pedido debe tener en la campaña en la
	 *	que fue facturado, el cual debe ser respetado al descontar productos
	 *	debido a la creación de notas.
	 *
	 * @returns
	 *	El monto mínimo permitido, computado sobre el precio total de folleto.
	 */
	public minimumOrderAmount(): Observable<number> {
		return this._minimumOrderAmount.asObservable();
	}

	/**
	 * @description
	 *	.
	 *
	 * @returns
	 *	.
	 */
	public needProductReplacement(): Observable<boolean> {
		return this._needProductReplacement.asObservable();
	}

	/**
	 * @description
	 *	.
	 *
	 * @returns
	 *	.
	 */
	public needProductReturn(): Observable<boolean> {
		return this._needProductReturn.asObservable();
	}

	/**
	 * @description
	 *	.
	 *
	 * @returns
	 *	.
	 */
	public order(): Observable<Optional<Order>> {
		return this._order.asObservable();
	}

	/**
	 * @description
	 *	.
	 *
	 * @returns
	 *	.
	 */
	public orderEventProducts(): Observable<MatTableDataSource<CampaignProduct>> {
		return this._orderEventProducts.asObservable()
			.pipe(
				tap(dataSource => dataSource.data
					.sort((campaignProduct1, campaignProduct2) => +campaignProduct1.product.code - +campaignProduct2.product.code)));
	}

	/**
	 * @description
	 *	Devuelve un observable sobre las entradas de seguimiento de la nota.
	 *
	 * @returns
	 *	DataSource de seguimiento de la nota.
	 */
	public orderEventTrackings(): Observable<MatTableDataSource<OrderEventTrackingForGet>> {
		return this._orderEventTrackings.asObservable();
	}

	/**
	 * @description
	 *	Devuelve un observable sobre los ítems agregados a esta nota.
	 *
	 * @returns
	 *	DataSource de los ítems asociados a la nota (los que requieren cambio
	 *	o acreditación).
	 */
	public orderItems(): Observable<MatTableDataSource<OrderItem>> {
		return this._orderItems.asObservable()
			.pipe(
				tap(dataSource => dataSource.data
					.sort((orderItem1, orderItem2) => +orderItem1.productCode - +orderItem2.productCode)));
	}

	/**
	 * @description
	 *	Método que debe ejecutarse para liberar los recursos que este motor ha
	 *	utilizado, principlamente sus observables. También se encarga de
	 *	completar sus emisores de eventos.
	 */
	public release(): void {
		this._amount.complete();
		this._availableOrderEventTypes.complete();
		this._campaign.complete();
		this._campaignProducts.complete();
		this._controlStatus.complete();
		this._discount.complete();
		this._discountDoesNotChange.complete();
		this._existingOrderEvents.complete();
		this._hasMinimumOrderAmount.complete();
		this._hasShippingAmountCovered.complete();
		this._isOverOriginalAmount.complete();
		this._isReadyForCreation.complete();
		this._minimumOrderAmount.complete();
		this._needProductReplacement.complete();
		this._needProductReturn.complete();
		this._order.complete();
		this._orderEventForGet.complete();
		this._orderEventProducts.complete();
		this._orderEventSearch.complete();
		this._orderItems.complete();
		this._orderEventTrackings.complete();
		this._remainingOrder.complete();
		this._replacementItems.complete();
		this._totalAmountAtBrochurePrice.complete();
		this._totalAmountAtInvoicePrice.complete();
		this._totalOrderEventBrochurePrice.complete();
		this._totalOrderEventInvoicePrice.complete();
		this._totalOrderEventProductsBrochurePrice.complete();
		this._totalReplacementItemsBrochurePrice.complete();
		this._unsubscriber.releaseSubscriptions();
	}

	/**
	 * @description
	 *	.
	 *
	 * @returns
	 *	.
	 */
	public replacementItems(): Observable<MatTableDataSource<CampaignProduct>> {
		return this._replacementItems.asObservable()
			.pipe(
				tap(dataSource => dataSource.data
					.sort((campaignProduct1, campaignProduct2) => +campaignProduct1.product.code - +campaignProduct2.product.code)));
	}

	/**
	 * @description
	 *	Devuelve el monto total del pedido tomando en cuenta los precios de
	 *	folleto de los productos restantes en el mismo (luego de descontar los
	 *	productos que poseen notas asociadas). El mismo se computa como un
	 *	observable que varía cuando se agregan o quitan ítems de la nota
	 *	automáticamente.
	 *
	 * @returns
	 *	El monto total del pedido asociado, según el precio de folleto.
	 */
	public totalAmountAtBrochurePrice(): Observable<number> {
		return this._totalAmountAtBrochurePrice.asObservable();
	}

	/**
	 * @description
	 *	Devuelve el monto total del pedido tomando en cuenta los precios de
	 *	facturación (precio a pagar), de los productos restantes en el mismo
	 *	(luego de descontar los productos que poseen notas asociadas). El
	 *	mismo se computa como un observable que varía cuando se agregan o
	 *	quitan ítems de la nota automáticamente.
	 *
	 * @returns
	 *	El monto total del pedido asociado, según el precio a pagar.
	 */
	public totalAmountAtInvoicePrice(): Observable<number> {
		return this._totalAmountAtInvoicePrice.asObservable();
	}

	/**
	 * @description
	 *	.
	 *
	 * @returns
	 *	.
	 */
	public totalOrderEventBrochurePrice(): Observable<number> {
		return this._totalOrderEventBrochurePrice.asObservable();
	}

	/**
	 * @description
	 *	.
	 *
	 * @returns
	 *	.
	 */
	public totalOrderEventInvoicePrice(): Observable<number> {
		return this._totalOrderEventInvoicePrice.asObservable();
	}

	/**
	 * @description
	 *	.
	 *
	 * @returns
	 *	.
	 */
	public totalOrderEventProductsBrochurePrice(): Observable<number> {
		return this._totalOrderEventProductsBrochurePrice.asObservable();
	}

	/**
	 * @description
	 *	.
	 *
	 * @returns
	 *	.
	 */
	public totalReplacementItemsBrochurePrice(): Observable<number> {
		return this._totalReplacementItemsBrochurePrice.asObservable();
	}

	// Métodos para agregar, borrar o disminuir ítems:
	public canAddItem(type: string, item: any, quantity: number): boolean {
		if (0 <= quantity && Optional.of(item).isPresent()) {
			switch (type) {
				case "campaignProduct": {
					return true;
				}
				case "orderItem": {
					return quantity <= this.quantity((item as OrderItem).id);
				}
				case "replacementItem": {
					return true;
				}
				default:
					this._logger.fatal("Unknown add-item type:", type);
			}
		}
		return false;
	}

	public canDeleteItem(type: string, item: any): boolean {
		switch (type) {
			case "campaignProduct": {
				const campaignProduct = item as CampaignProduct;
				return this.canRemoveItem(type, campaignProduct, this.orderEventProductQuantity(campaignProduct));
			}
			case "orderItem": {
				const orderItem = item as OrderItem;
				return this.canRemoveItem(type, orderItem, orderItem.quantity);
			}
			case "replacementItem": {
				const replacementItem = item as CampaignProduct;
				return this.canRemoveItem(type, replacementItem, this.replacementItemQuantity(replacementItem));
			}
			default:
				this._logger.fatal("Unknown delete-item type:", type);
		}
		return false;
	}

	public canRemoveItem(type: string, item: any, quantity: number): boolean {
		if (0 < quantity && Optional.of(item).isPresent()) {
			switch (type) {
				case "campaignProduct": {
					const campaignProduct = item as CampaignProduct;
					return quantity <= this.orderEventProductQuantity(campaignProduct);
				}
				case "orderItem": {
					return quantity <= (item as OrderItem).quantity;
				}
				case "replacementItem": {
					const replacementItem = item as CampaignProduct;
					return quantity <= this.replacementItemQuantity(replacementItem);
				}
				default:
					this._logger.fatal("Unknown remove-item type:", type);
			}
		}
		return false;
	}

	// Cada 'case' debería representarse como una clase que contenga los ítems
	// y los gestione, a modo de repositorio o colección. Cada clase separada
	// debe implementar la misma interfaz, es decir, una que ofrezca inserción
	// y borrado de ítems.
	public onAddItem(type: string, item: any, quantity: number): void {
		if (this.canAddItem(type, item, quantity)) {
			this._logger.debug("add-item:", type, item, quantity);
			switch (type) {
				case "campaignProduct": {
					const campaignProduct = CampaignProduct.from(item);
					const orderEventProducts = this.mergeCampaignProduct(this._orderEventProducts.value.data, campaignProduct, quantity);
					this._orderEventProducts.next(new MatTableDataSource(orderEventProducts));
					break;
				}
				case "orderItem": {
					const orderItem = (item as OrderItem);
					this.remainingOrderItem(orderItem.id)
						.map(orderItem => {
							orderItem.quantity -= quantity;
							const newOrderItem = OrderItem.from(orderItem);
							newOrderItem.quantity = quantity;
							return OrderEventEngine.mergeOrderItem(this._orderItems.value.data, newOrderItem);
						})
						.ifPresent(orderItems => this._orderItems.next(new MatTableDataSource(orderItems)));
					OrderEventEngine.trigger(this._remainingOrder);
					if (this._causesWithAutoInsertion.has(this._advancedForm.valueOf("orderEventTypeId"))) {
						if (orderItem.paymentCycle > 1) {
							this._campaignProductService.getForPaymentCycle(orderItem.campaignProductId).subscribe(replacementItems => {
								this.onAddItem("replacementItem", replacementItems[0], quantity);
							});
						}
						else {
							this.onAddItem("replacementItem", this._campaignProducts.value
							.find(campaignProduct => campaignProduct.product.code == orderItem.productCode), quantity);
						}		
					}
					break;
				}
				case "replacementItem": {
					const replacementItem = CampaignProduct.from(item);
					const replacementItems = this.mergeReplacementItem(this._replacementItems.value.data, replacementItem, quantity);
					this._replacementItems.next(new MatTableDataSource(replacementItems));
					break;
				}
				default:
					this._logger.fatal("Unknown add-item type:", type);
			}
		}
	}

	public onDeleteItem(type: string, item: any): void {
		if (this.canDeleteItem(type, item)) {
			this._logger.debug("delete-item:", type, item);
			switch (type) {
				case "campaignProduct": {
					const quantity = this.orderEventProductQuantity(item as CampaignProduct);
					this.onRemoveItem(type, item, quantity);
					break;
				}
				case "orderItem": {
					this.onRemoveItem(type, item, (item as OrderItem).quantity);
					break;
				}
				case "replacementItem": {
					const quantity = this.replacementItemQuantity(item as CampaignProduct);
					this.onRemoveItem(type, item, quantity);
					break;
				}
				default:
					this._logger.fatal("Unknown delete-item type:", type);
			}
		}
	}

	public onRemoveItem(type: string, item: any, quantity: number): void {
		if (this.canRemoveItem(type, item, quantity)) {
			this._logger.debug("remove-item:", type, item, quantity);
			switch (type) {
				case "campaignProduct": {
					const campaignProduct = item as CampaignProduct;
					const oldQuantity = this.orderEventProductQuantity(campaignProduct);
					this.orderEventProductQuantities.set(campaignProduct.id, oldQuantity - quantity);
					if (this.orderEventProductQuantity(campaignProduct) === 0) {
						const orderEventProducts = OrderEventEngine.removeCampaignProduct(this._orderEventProducts.value.data, campaignProduct);
						this._orderEventProducts.next(new MatTableDataSource(orderEventProducts));
					}
					else {
						OrderEventEngine.trigger(this._orderEventProducts);
					}
					break;
				}
				case "orderItem": {
					const orderItemToRemove = item as OrderItem;
					orderItemToRemove.quantity -= quantity;
					this.remainingOrderItem(orderItemToRemove.id)
						.ifPresent(orderItem => orderItem.quantity += quantity);
					if (orderItemToRemove.quantity === 0) {
						const orderItems = OrderEventEngine.removeOrderItem(this._orderItems.value.data, orderItemToRemove);
						this._orderItems.next(new MatTableDataSource(orderItems));
					}
					else {
						OrderEventEngine.trigger(this._orderItems);
					}
					OrderEventEngine.trigger(this._remainingOrder);
					if (this._causesWithAutoInsertion.has(this._advancedForm.valueOf("orderEventTypeId"))) {
						if (orderItemToRemove.paymentCycle > 1) {	
							this._campaignProductService.getForPaymentCycle(orderItemToRemove.campaignProductId).subscribe(replacementItems => {
								this.onRemoveItem("replacementItem", replacementItems[0], quantity);
							});
						}
						else	{
							this.onRemoveItem("replacementItem", this._campaignProducts.value
							.find(campaignProduct => campaignProduct.product.code == orderItemToRemove.productCode), quantity);
						}	
					}
					break;
				}
				case "replacementItem": {
					const replacementItem = item as CampaignProduct;
					const oldQuantity = this.replacementItemQuantity(replacementItem);
					this.replacementItemQuantities.set(replacementItem.id, oldQuantity - quantity);
					if (this.replacementItemQuantity(replacementItem) === 0) {
						const replacementItems = OrderEventEngine.removeCampaignProduct(this._replacementItems.value.data, replacementItem);
						this._replacementItems.next(new MatTableDataSource(replacementItems));
					}
					else {
						OrderEventEngine.trigger(this._replacementItems);
					}
					break;
				}
				default:
					this._logger.fatal("Unknown remove-item type:", type);
			}
		}
	}

	public orderEventProductQuantity(campaignProduct: CampaignProduct): number {
		return Optional
			.of(this.orderEventProductQuantities.get(campaignProduct.id))
			.getOrElse(0);
	}

	public orderEventProductTotalBrochurePrice(campaignProduct: CampaignProduct): number {
		return campaignProduct.price * this.orderEventProductQuantity(campaignProduct);
	}

	public replacementItemQuantity(campaignProduct: CampaignProduct): number {
		return Optional
			.of(this.replacementItemQuantities.get(campaignProduct.id))
			.getOrElse(0);
	}

	// Para mostrar, no para computar (habría que normalizar para que siempre
	// muestre el de campaña en los reemplazos):
	public replacementItemTotalBrochurePrice(campaignProduct: CampaignProduct): number {
		const price = this._order.value
			.flatMap(order => order.findOrderItemByCode(campaignProduct.product.code))
			.map(orderItem => orderItem.price)
			.getOrElse(campaignProduct.price);
		return price * this.replacementItemQuantity(campaignProduct);
	}

	/**
	 * @description
	 *	Computa la cantidad remanente de un ítem del pedido actual. Si el ítem
	 *	no existe, entonces su cantidad remanente debe ser cero.
	 *
	 * @param orderItemId
	 *	El ID del ítem del pedido.
	 *
	 * @returns
	 *	La cantidad disponible en el pedido para agregar a la nota.
	 */
	private quantity(orderItemId: number): number {
		return this.remainingOrderItem(orderItemId)
			.map(orderItem => orderItem.quantity)
			.getOrElse(0);
	}

	/**
	 * @description
	 *	Encuentra un ítem en el pedido restante (el pedido con las notas
	 *	existentes descontadas). Nótese que el ítem permanece en este pedido
	 *	remanente aún cuando su cantidad es igual que 0.
	 *
	 * @param orderItemId
	 *	El ID del ítem del pedido a buscar.
	 *
	 * @returns
	 *	El modelo del ítem del pedido remanente. Este modelo no es una copia,
	 *	por lo que las modificaciones sobre el mismo modifican el ítem
	 *	original, lo cual es el comportamiento esperado.
	 */
	private remainingOrderItem(orderItemId: number): Optional<OrderItem> {
		return this._remainingOrder.value
			.flatMap(order => order.findOrderItem(orderItemId))
	}

	private mergeCampaignProduct(campaignProducts: CampaignProduct[], campaignProduct: CampaignProduct, quantityToAdd: number): CampaignProduct[] {
		Optional.of(campaignProducts.find(_campaignProduct => _campaignProduct.id === campaignProduct.id))
			.ifPresent(_campaignProduct => {
				const quantity = this.orderEventProductQuantity(campaignProduct);
				this.orderEventProductQuantities.set(campaignProduct.id, quantity + quantityToAdd);
			})
			.orElse(() => {
				campaignProducts.push(campaignProduct);
				this.orderEventProductQuantities.set(campaignProduct.id, quantityToAdd);
			});
		return campaignProducts;
	}

	private mergeReplacementItem(campaignProducts: CampaignProduct[], campaignProduct: CampaignProduct, quantityToAdd: number): CampaignProduct[] {
		Optional.of(campaignProducts.find(_campaignProduct => _campaignProduct.id === campaignProduct.id))
			.ifPresent(_campaignProduct => {
				const quantity = this.replacementItemQuantity(campaignProduct);
				this.replacementItemQuantities.set(campaignProduct.id, quantity + quantityToAdd);
			})
			.orElse(() => {
				campaignProducts.push(campaignProduct);
				this.replacementItemQuantities.set(campaignProduct.id, quantityToAdd);
			});
		return campaignProducts;
	}

	private static mergeOrderItem(orderItems: OrderItem[], orderItem: OrderItem): OrderItem[] {
		Optional.of(orderItems.find(_orderItem => _orderItem.id === orderItem.id))
			.ifPresent(_orderItem => _orderItem.quantity += orderItem.quantity)
			.orElse(() => orderItems.push(orderItem));
		return orderItems;
	}

	private static removeCampaignProduct(campaignProducts: CampaignProduct[], campaignProduct: CampaignProduct): CampaignProduct[] {
		const index = campaignProducts.findIndex(_campaignProduct => _campaignProduct.id === campaignProduct.id);
		if (0 <= index) {
			campaignProducts.splice(index, 1);
		}
		return campaignProducts;
	}

	private static removeOrderItem(orderItems: OrderItem[], orderItem: OrderItem): OrderItem[] {
		const index = orderItems.findIndex(_orderItem => _orderItem.id === orderItem.id);
		if (0 <= index) {
			orderItems.splice(index, 1);
		}
		return orderItems;
	}

	/**
	 * @description
	 *	Emite el último payload de un observable nuevamente, con el efecto de
	 *	realizar una nueva propagación del mismo y de sus demás consumidores
	 *	registrados.
	 *
	 * @param subject
	 *	El comportamiento a relanzar.
	 *
	 * @returns
	 *	Devuelve el mismo comportamiento para facilitar el method-chaining.
	 */
	private static trigger<T>(subject: BehaviorSubject<T>): BehaviorSubject<T> {
		subject.next(subject.value);
		return subject;
	}
}

	/**
	 * @description
	 *	Patrón builder para la clase OrderEventEngine.
	 */

export class OrderEventEngineBuilder {

	public _advancedForm: AdvancedForm;
	public _injector: Injector;
	public _logger: Logger;

	public constructor() {
		this._advancedForm = undefined;
		this._injector = undefined;
		this._logger = undefined;
	}

	public build(): OrderEventEngine {
		return new OrderEventEngine(this);
	}

	public advancedForm(advancedForm: AdvancedForm): OrderEventEngineBuilder {
		this._advancedForm = advancedForm;
		return this;
	}

	public injector(injector: Injector): OrderEventEngineBuilder {
		this._injector = injector;
		return this;
	}

	public logger(logger: Logger): OrderEventEngineBuilder {
		this._logger = logger;
		return this;
	}
}
